import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react'

const SCRIPT_ID = 'cf-turnstile-script'
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

// Stamped on every widget so the integration can be attributed in analytics.
const WIDGET_ACTION = 'turnstile-spin-v2'

let scriptPromise = null

// One script tag per page, shared by every widget on it. Re-injecting it is
// what makes Turnstile leak <script> tags and eventually blow the call stack.
const loadTurnstileScript = () => {
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')), {
        once: true,
      })
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('turnstile script failed'))
    document.head.appendChild(script)
  }).catch(error => {
    // Let a later mount retry rather than caching the failure forever.
    scriptPromise = null
    throw error
  })

  return scriptPromise
}

/**
 * Cloudflare Turnstile challenge.
 *
 * Renders nothing when no site key is configured — the backend then sees a
 * request with no token, allows it, and records that nothing was verified.
 * That keeps a missing key a degraded check rather than a broken form.
 *
 * Tokens are single use. Any request that carries one spends it, so callers
 * hold a ref and call reset() before the user can submit again — otherwise
 * the retry sends a spent token and is refused as a duplicate.
 */
const TurnstileInner = ({ siteKey, onToken, className }, ref) => {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const onTokenRef = useRef(onToken)

  useEffect(() => {
    onTokenRef.current = onToken
  }, [onToken])

  const reset = useCallback(() => {
    if (window.turnstile && widgetIdRef.current) {
      window.turnstile.reset(widgetIdRef.current)
      onTokenRef.current(null)
    }
  }, [])

  useImperativeHandle(ref, () => ({ reset }), [reset])

  useEffect(() => {
    if (!siteKey || !containerRef.current) return undefined

    let cancelled = false

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !containerRef.current) return

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: WIDGET_ACTION,
          callback: token => onTokenRef.current(token),
          'error-callback': () => onTokenRef.current(null),
          'expired-callback': () => onTokenRef.current(null),
        })
      })
      .catch(() => {
        // Blocked script, offline, ad blocker: the form stays usable and the
        // backend decides what an unverified request means.
        if (!cancelled) onTokenRef.current(null)
      })

    return () => {
      cancelled = true
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [siteKey])

  if (!siteKey) return null

  return <div ref={containerRef} className={className} data-action={WIDGET_ACTION} />
}

export default forwardRef(TurnstileInner)
